import { useEffect, useRef, useState } from 'react';

import { CLARIFICATION_TYPED_ANSWER_MAX_LENGTH } from '../../../../../../../../store/dashboard/askAica/constants';
import { ClarificationAnswer, ClarificationCard } from '../../../../../../../../store/dashboard/askAica/clarificationTypes';

interface Params {
    card: ClarificationCard;
    /** Called once, with one answer per question, the moment the last question is answered. */
    onSubmit: (answers: ClarificationAnswer[]) => void;
}

export const useClarificationCardLogic = ({ card, onSubmit }: Params) => {
    const { questions } = card;
    const [picked, setPicked] = useState<Record<string, ClarificationAnswer>>({});
    const [index, setIndex] = useState(0);
    const [isTyping, setIsTyping] = useState(false);
    const [draft, setDraft] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);

    const question = questions[index];
    const current = picked[question.id];
    const pickedOptionId = current && 'option_id' in current ? current.option_id : undefined;
    const typedText = current && 'text' in current ? current.text : undefined;

    useEffect(() => {
        if (isTyping) inputRef.current?.focus();
    }, [isTyping]);

    const goTo = (target: number) => {
        setIndex(Math.min(Math.max(target, 0), questions.length - 1));
        setIsTyping(false);
    };

    // Records the current question's answer, then auto-advances to the next unanswered one
    // (wrapping, so a back-edit lands on whatever is still open) — or sends the round when none is.
    const commit = (answer: ClarificationAnswer) => {
        const next = { ...picked, [question.id]: answer };
        setPicked(next);
        setIsTyping(false);
        const order = [...questions.slice(index + 1), ...questions.slice(0, index + 1)];
        const nextOpen = order.find((candidate) => !next[candidate.id]);
        if (nextOpen) {
            setIndex(questions.indexOf(nextOpen));
            return;
        }
        onSubmit(questions.map((candidate) => next[candidate.id]));
    };

    const selectOption = (optionId: string) => commit({ question_id: question.id, option_id: optionId });

    const startTyping = () => {
        setDraft(typedText ?? '');
        setIsTyping(true);
    };

    const updateDraft = (value: string) => setDraft(value.slice(0, CLARIFICATION_TYPED_ANSWER_MAX_LENGTH));

    const submitTyped = () => {
        const text = draft.trim();
        if (text) commit({ question_id: question.id, text });
    };

    return {
        question,
        index,
        total: questions.length,
        pickedOptionId,
        typedText,
        isTyping,
        draft,
        inputRef,
        goTo,
        selectOption,
        startTyping,
        updateDraft,
        submitTyped,
    };
};
